import { useState, useEffect } from 'react';
import Modal from '@components/ui/general/Modal/Modal';
import { Bug, Lightbulb, HelpCircle, AlertTriangle, Loader2, Camera, Trash2 } from 'lucide-react';
import { submitBugReportApi } from '@api/bugReportApi';
import { captureMapScreenshot } from '@utils/screenshotCapture';
import { getLogsForReport } from '@utils/consoleCapture';
import useGameStore from '@store/useGameStore';
import useAuthStore from '@store/useAuthStore';
import useSelectionStore from '@store/useSelectionStore';
import feedback from '@components/ui/general/feedback/feedback';
import './BugReportModal.css';

/**
 * Bug Report Modal for beta testing
 * Captures screenshot, game state, and console logs
 */
export default function BugReportModal({ isOpen, onClose }) {
    const [category, setCategory] = useState('bug');
    const [severity, setSeverity] = useState('medium');
    const [description, setDescription] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [includeDebugData, setIncludeDebugData] = useState(true);

    const game = useGameStore((state) => state.game);

    // Auto-capture screenshot when modal opens
    useEffect(() => {
        if (isOpen && !screenshot) {
            handleCaptureScreenshot();
        }
    }, [isOpen]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCategory('bug');
            setSeverity('medium');
            setDescription('');
            setScreenshot(null);
            setIncludeDebugData(true);
        }
    }, [isOpen]);

    const handleCaptureScreenshot = async () => {
        setIsCapturing(true);
        try {
            const captured = await captureMapScreenshot();
            setScreenshot(captured);
        } catch (error) {
            console.error('[BugReportModal] Screenshot capture failed:', error);
            feedback.error('Failed to capture screenshot');
        } finally {
            setIsCapturing(false);
        }
    };

    const handleRemoveScreenshot = () => {
        setScreenshot(null);
    };

    const collectGameState = () => {
        if (!includeDebugData) return null;

        try {
            const gameState = useGameStore.getState();
            const selectionState = useSelectionStore.getState();

            return {
                game: gameState.game ? {
                    id: gameState.game.id,
                    status: gameState.game.status,
                    turn: gameState.game.turn,
                    players: gameState.game.players?.map(p => ({
                        id: p.id,
                        name: p.name,
                        team: p.pivot?.team
                    })),
                    units_count: gameState.game.dynamic_objects?.units?.length || 0,
                } : null,
                selection: {
                    mode: selectionState.mode,
                    currentClick: selectionState.currentClick ? {
                        type: selectionState.currentClick.type,
                        objectId: selectionState.currentClick.object?.id
                    } : null
                },
                snapshots_available: gameState._rawGame?.snapshots ? Object.keys(gameState._rawGame.snapshots) : []
            };
        } catch (error) {
            console.error('[BugReportModal] Failed to collect game state:', error);
            return { error: 'Failed to collect game state' };
        }
    };

    const collectMetadata = () => {
        return {
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    };

    const handleSubmit = async () => {
        if (!description.trim()) {
            feedback.warning('Please enter a description');
            return;
        }

        setIsSubmitting(true);

        try {
            const { user, activePlayer } = useAuthStore.getState();

            const reportData = {
                category,
                severity,
                description: description.trim(),
                url: window.location.href,
                user_name: user?.name || null,
                game_id: game?.id || null,
                player_name: activePlayer?.name || null,
                game_state: collectGameState(),
                console_logs: includeDebugData ? getLogsForReport() : null,
                screenshot: screenshot,
                metadata: collectMetadata(),
            };

            await submitBugReportApi(reportData);
            feedback.success('Report sent successfully. Thank you!');
            onClose();
        } catch (error) {
            console.error('[BugReportModal] Submit failed:', error);
            feedback.error('Failed to send report. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const categoryOptions = [
        {
            value: 'bug',
            label: 'Bug',
            icon: Bug,
            description: 'Something is broken or not working as expected',
        },
        {
            value: 'suggestion',
            label: 'Suggestion',
            icon: Lightbulb,
            description: 'Ideas to improve the game',
        },
        {
            value: 'confusion',
            label: 'Confusion',
            icon: HelpCircle,
            description: "Something that's unclear or hard to understand",
        },
    ];

    const severityOptions = [
        {
            value: 'critical',
            label: 'Critical',
            color: 'var(--color-red)',
            description: 'Game-breaking, blocks progress',
        },
        {
            value: 'medium',
            label: 'Medium',
            color: 'var(--color-orange)',
            description: 'Annoying but has workaround',
        },
        {
            value: 'low',
            label: 'Low',
            color: 'var(--color-gold-bright)',
            description: 'Minor issue, cosmetic',
        },
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Report Issue"
            className="bug-report-modal"
        >
            <div className="bug-report-content">
                {/* Category Section */}
                <div className="bug-report-section">
                    <h4 className="bug-report-section-title">Category</h4>
                    <div className="bug-report-options bug-report-options--horizontal">
                        {categoryOptions.map((option) => {
                            const Icon = option.icon;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`bug-report-option bug-report-option--category ${category === option.value ? 'bug-report-option--selected' : ''}`}
                                    onClick={() => setCategory(option.value)}
                                >
                                    <Icon size={20} />
                                    <span className="bug-report-option-label">{option.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Severity Section */}
                <div className="bug-report-section">
                    <h4 className="bug-report-section-title">
                        <AlertTriangle size={16} />
                        Severity
                    </h4>
                    <div className="bug-report-options bug-report-options--horizontal">
                        {severityOptions.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className={`bug-report-option bug-report-option--severity ${severity === option.value ? 'bug-report-option--selected' : ''}`}
                                style={{ '--severity-color': option.color }}
                                onClick={() => setSeverity(option.value)}
                            >
                                <span className="bug-report-option-label">{option.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Description Section */}
                <div className="bug-report-section">
                    <h4 className="bug-report-section-title">Description</h4>
                    <textarea
                        className="bug-report-textarea"
                        placeholder="Describe what happened, what you expected, and steps to reproduce..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        maxLength={5000}
                    />
                    <span className="bug-report-char-count">{description.length}/5000</span>
                </div>

                {/* Screenshot Section */}
                <div className="bug-report-section">
                    <h4 className="bug-report-section-title">
                        <Camera size={16} />
                        Screenshot
                    </h4>
                    <div className="bug-report-screenshot-container">
                        {isCapturing ? (
                            <div className="bug-report-screenshot-loading">
                                <Loader2 size={24} className="spinner" />
                                <span>Capturing...</span>
                            </div>
                        ) : screenshot ? (
                            <div className="bug-report-screenshot-preview">
                                <img src={screenshot} alt="Screenshot preview" />
                                <div className="bug-report-screenshot-actions">
                                    <button
                                        type="button"
                                        className="button bug-report-screenshot-btn"
                                        onClick={handleCaptureScreenshot}
                                        title="Retake screenshot"
                                    >
                                        <Camera size={14} />
                                        Retake
                                    </button>
                                    <button
                                        type="button"
                                        className="button bug-report-screenshot-btn bug-report-screenshot-btn--danger"
                                        onClick={handleRemoveScreenshot}
                                        title="Remove screenshot"
                                    >
                                        <Trash2 size={14} />
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="button bug-report-capture-btn"
                                onClick={handleCaptureScreenshot}
                            >
                                <Camera size={16} />
                                Capture Screenshot
                            </button>
                        )}
                    </div>
                </div>

                {/* Debug Data Toggle */}
                <div className="bug-report-section bug-report-section--checkbox">
                    <label className="bug-report-checkbox">
                        <input
                            type="checkbox"
                            checked={includeDebugData}
                            onChange={(e) => setIncludeDebugData(e.target.checked)}
                        />
                        <span>Include debug data (game state, console logs)</span>
                    </label>
                </div>

                {/* Actions */}
                <div className="bug-report-actions">
                    <button
                        type="button"
                        className="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="button button-cta"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !description.trim()}
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 size={16} className="spinner" />
                                Sending...
                            </>
                        ) : (
                            'Send Report'
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
